import React, { useEffect, useRef, useState } from 'react';
import { WifiOff, Check } from 'lucide-react';
import api from '../lib/api';

/**
 * Admin-only banner that polls /api/health and surfaces backend outages.
 *
 * Three states:
 *   ok          → render nothing
 *   down        → red "Reconnecting…" pill, faster retries
 *   reconnected → green "Connection restored" flash for ~4s then disappears
 *
 * The fast-retry mode catches the 5-10s 502 window that happens during
 * `supervisorctl restart backend` so the admin sees a clear "wait, retrying"
 * affordance instead of cryptic Axios errors when they click around.
 */
const HEALTHY_INTERVAL_MS = 15000;
const DOWN_INTERVAL_MS = 3000;
const RECONNECTED_FLASH_MS = 4000;

export default function BackendHealthBanner() {
  const [status, setStatus] = useState('ok'); // 'ok' | 'down' | 'reconnected'
  const timerRef = useRef(null);
  const statusRef = useRef('ok');
  statusRef.current = status;

  useEffect(() => {
    let cancelled = false;

    async function ping() {
      try {
        await api.get('/health', { timeout: 4000 });
        if (cancelled) return;
        if (statusRef.current === 'down') {
          setStatus('reconnected');
          setTimeout(() => {
            if (!cancelled) setStatus('ok');
          }, RECONNECTED_FLASH_MS);
        } else if (statusRef.current !== 'reconnected') {
          setStatus('ok');
        }
      } catch (_e) {
        if (cancelled) return;
        if (statusRef.current !== 'down') setStatus('down');
      } finally {
        if (cancelled) return;
        const next = statusRef.current === 'down' ? DOWN_INTERVAL_MS : HEALTHY_INTERVAL_MS;
        timerRef.current = setTimeout(ping, next);
      }
    }

    ping();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (status === 'ok') return null;

  if (status === 'reconnected') {
    return (
      <div
        data-testid="backend-health-banner-reconnected"
        className="fixed top-4 right-4 z-[60] bg-forest text-base px-4 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 shadow-soft"
        role="status"
      >
        <Check size={14}/> Connection restored.
      </div>
    );
  }

  // status === 'down'
  return (
    <div
      data-testid="backend-health-banner-down"
      className="fixed top-4 right-4 z-[60] bg-heritage text-base px-4 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 shadow-soft"
      role="alert"
    >
      <WifiOff size={14}/> Backend down — reconnecting…
    </div>
  );
}

// Named export kept available too — eslint-friendly.
export { BackendHealthBanner };
